import React from "react";
import type Widget from "@/model/widget";
import WidgetCard from "./widget-card";
import { Button } from "@/components/ui/button";
import { CircleArrowRight } from "lucide-react";
import { useIDCardQuery } from "@/hooks/use-id-card";
import IDCardSkeletonLoader from "../id-card/id-card-skeleton-loader";
import ScreenRouteNames from "@/constants/screen-route-names";
import { useNavigate } from "react-router-dom";
import PDFViewer from "../base64-pdf-viewer/base64-pdf-viewer";
import { NoDataAvailable, SomethingWentWrong } from "@/pages/components/empty";
import { ID_CARD_KEY_BASE64_KEY } from "@/constants";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { useFeatureName } from "@/hooks/use-pageTitle";
import { updateRequestedAt } from "@/store/request-id-card/requestId-slice";

type IdCardWidgetProps = {
  className?: string;
  widget: Widget;
};

const IdCardWidget: React.FunctionComponent<IdCardWidgetProps> = ({
  widget: _widget,
}) => {
  const navigate = useNavigate();
  const { activeCoverage } = useSelector(
    (state: RootState) => state.planCoverageInfo,
  );
  const { requestedAt } = useSelector((state: RootState) => state.requestId);

  const {
    data: idCardData,
    isFetching: isIDCardFetching,
    isFetched: isIDCardFetched,
    isError,
    error,
    isSuccess: isIdCardSuccess,
  } = useIDCardQuery(activeCoverage);

  const dispatch = useDispatch();

  if (isIdCardSuccess)
    dispatch(
      updateRequestedAt({
        requestedAt: requestedAt || idCardData?.idCardRequestedAt,
        isLoaded: true,
      }),
    );

  const renderWidget = () => {
    if (isIDCardFetching) {
      return <IDCardSkeletonLoader />;
    }
    if (isError) return <SomethingWentWrong message={error.message} />;
    if (isIDCardFetched) {
      if (
        ID_CARD_KEY_BASE64_KEY in idCardData! &&
        idCardData?.base64Encoded?.length > 0
      ) {
        return (
          <PDFViewer base64Encoded={idCardData.base64Encoded} pageNumber={1} />
        );
      } else {
        return (
          <NoDataAvailable
            title="Unable to Render ID Card"
            message={idCardData?.message}
          />
        );
      }
    }
  };

  const viewFullInsuranceCard = () => {
    const targetPath = `/${ScreenRouteNames.ID_CARD.path}`;
    navigate(targetPath, {
      replace: false,
    });
  };

  const footerContent = (
    <Button
      variant="link"
      className="w-full hover:underline"
      onClick={viewFullInsuranceCard}
    >
      View Insurance Card(s)
      <CircleArrowRight className="ml-1" />
    </Button>
  );

  return (
    <WidgetCard
      title={useFeatureName(ScreenRouteNames.ID_CARD.path)}
      footerContent={footerContent}
    >
      {renderWidget()}
    </WidgetCard>
  );
};

export default IdCardWidget;
